import React, { useState } from "react";
import axios from "axios";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Email, ArrowBack } from "@mui/icons-material";
import { toast } from "react-toastify";
import "./ForgotPassword.css";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Create FormData for application/x-www-form-urlencoded
    const formData = new FormData();
    formData.append("email", email.trim());

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      
      // Validate API URL exists
      if (!API_BASE_URL) {
        throw new Error('API_BASE_URL is not defined in .env');
      }

      const forgotPasswordUrl = `${API_BASE_URL}/forgot-password`;

      // Validate URL format
      try {
        new URL(forgotPasswordUrl);
      } catch (urlError) {
        throw new Error(`Invalid API URL: ${forgotPasswordUrl}`);
      }

      const response = await axios.post(forgotPasswordUrl, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      // Success response
      toast.success(
        response.data.message || "Email sent! Please check your email for your temporary password.",
        {
          autoClose: 4000,
        }
      );

      setEmail("");
    } catch (err) {
      console.error("Password reset error:", err);
      console.error("Response data:", err.response?.data);

      let errorMessage = "Failed to send reset email. Please try again.";

      if (err.response) {
        const { status, data } = err.response;
        
        if (status === 404) {
          errorMessage = "Email address not found. Please check and try again.";
        } else if (status === 422) {
          errorMessage = data?.detail?.[0]?.msg || "Invalid email format.";
        } else if (status === 500) {
          errorMessage = "Server error. Please try again later or contact support.";
        } else if (data?.detail) {
          errorMessage = typeof data.detail === 'string' 
            ? data.detail 
            : "An error occurred. Please try again.";
        }
      } else if (err.message.includes("Network Error")) {
        errorMessage = "Network error: Unable to connect to the server. Check your connection or server status.";
      } else if (err.message.includes("Invalid API URL")) {
        errorMessage = "Invalid API URL configuration. Please contact support.";
      }

      setError(errorMessage);
      toast.error(`Reset failed: ${errorMessage}`, {
        autoClose: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="forgot-password-container" maxWidth={false}>
      <Box className="forgot-password-grid">
        {/* Left Side - Branding */}
        <Box className="forgot-password-brand-section">
          <Box className="brand-content">
            <Typography variant="h2" className="brand-title">
              Password Recovery
            </Typography>
            <Typography variant="body1" className="brand-subtitle">
              Quick & Secure Access Recovery
            </Typography>
            <Box className="brand-features">
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">AI-powered document extraction</Typography>
              </Box>
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">Complete transaction chain visibility</Typography>
              </Box>
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">Encrypted data protection</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Side - Reset Form */}
        <Box className="forgot-password-form-section">
          <Paper className="forgot-password-paper" elevation={0}>
            <Box className="form-header">
              <Typography variant="h4" className="form-title">
                Forgot Password?
              </Typography>
              <Typography variant="body2" className="form-subtitle">
                Enter your email address and we'll send you a temporary password
              </Typography>
            </Box>

            {error && (
              <Box className="error-message">
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            <form onSubmit={handleReset}>
              <Box className="input-group">
                <TextField
                  label="Email Address"
                  variant="outlined"
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  required
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email className="input-icon" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                className="reset-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  "Send Temporary Password"
                )}
              </Button>
            </form>

            <Divider className="divider">
              <Typography variant="body2" className="divider-text">
                OR
              </Typography>
            </Divider>

            <Box className="back-to-login">
              <Link to="/login" className="back-link">
                <ArrowBack className="back-icon" />
                Back to Login
              </Link>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default ForgotPassword;